import { useMemo } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import HelmetTags from "../../../MainComponents/HelmetTags";
import Loader from "../../../MainComponents/Loader.jsx";
import useAxios from "../../../MainComponents/Hooks/useAxios.jsx";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

function StudentMyResults() {
  const [resultsData, , loading] = useAxios(
    process.env.REACT_APP_STUDENT_MY_RESULTS_API,
    "GET",
    "GET"
  );

  const points = resultsData?.data || [];

  const chartData = useMemo(() => {
    const labels = points.map(
      (item, index) => item.title || `Lecture ${index + 1}`
    );

    return {
      labels,
      datasets: [
        {
          label: "Quiz average %",
          data: points.map((item) => item.average),
          borderColor: "#00c2ce",
          backgroundColor: "rgba(0, 194, 206, 0.18)",
          pointBackgroundColor: "#00c2ce",
          pointBorderColor: "#ffffff",
          pointBorderWidth: 2,
          pointRadius: 6,
          pointHoverRadius: 8,
          fill: true,
          tension: 0.35,
        },
      ],
    };
  }, [points]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          labels: {
            color: "#94a3b8",
            font: { weight: "600" },
          },
        },
        tooltip: {
          callbacks: {
            title: (items) => {
              const index = items[0]?.dataIndex;
              return points[index]?.title || "";
            },
            label: (ctx) => {
              const point = points[ctx.dataIndex];
              const avg = Number(ctx.parsed.y).toFixed(1);
              const quizzes = point?.quizzes_count || 0;
              return `Average: ${avg}% (${quizzes} quiz${quizzes === 1 ? "" : "zes"})`;
            },
          },
        },
      },
      scales: {
        x: {
          ticks: {
            color: "#94a3b8",
            maxRotation: 45,
            minRotation: 0,
            callback(value) {
              const label = this.getLabelForValue(value);
              return label?.length > 18 ? `${label.slice(0, 18)}…` : label;
            },
          },
          grid: { color: "rgba(148, 163, 184, 0.12)" },
        },
        y: {
          min: 0,
          max: 100,
          ticks: {
            color: "#94a3b8",
            callback: (value) => `${value}%`,
          },
          grid: { color: "rgba(148, 163, 184, 0.12)" },
          title: {
            display: true,
            text: "Average score (%)",
            color: "#94a3b8",
          },
        },
      },
    }),
    [points]
  );

  const overallAverage = useMemo(() => {
    if (!points.length) return null;
    const sum = points.reduce((acc, item) => acc + Number(item.average || 0), 0);
    return (sum / points.length).toFixed(1);
  }, [points]);

  return (
    <section className="student-results-page width pt-28 pb-16">
      <HelmetTags title="My Results | Mr.Mohamed Salama" />

      <div className="student-results-header">
        <h1>My Results</h1>
        <p>Average quiz score for each lecture</p>
      </div>

      {loading && <Loader />}

      {!loading && points.length === 0 && (
        <div className="student-results-empty">
          <h2>No results yet</h2>
          <p>Take some lecture quizzes and your averages will show here.</p>
        </div>
      )}

      {!loading && points.length > 0 && (
        <>
          <div className="student-results-summary">
            <div className="student-results-stat">
              <span>Lectures</span>
              <strong>{points.length}</strong>
            </div>
            <div className="student-results-stat">
              <span>Overall average</span>
              <strong>{overallAverage}%</strong>
            </div>
          </div>

          <div className="student-results-chart-card">
            <div className="student-results-chart">
              <Line data={chartData} options={chartOptions} />
            </div>
          </div>

          <ul className="student-results-list">
            {points.map((item) => (
              <li key={item.course_id}>
                <div>
                  <h3>{item.title}</h3>
                  <p>
                    {item.quizzes_count} quiz
                    {item.quizzes_count === 1 ? "" : "zes"}
                    {item.branch ? ` · ${item.branch}` : ""}
                  </p>
                </div>
                <strong>{Number(item.average).toFixed(1)}%</strong>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

export default StudentMyResults;
